import { db } from './config';
import {
  collection,
  query,
  where,
  orderBy,
  limit as fsLimit,
  getDocs,
} from 'firebase/firestore';
import type { AIMedication } from '@/types/ai';
import { desmameField } from '@/utils/desmame';

/**
 * Coleção `medications` — global (top-level), não escopada por clínica.
 * Populada pelo script scripts/medications-seed (banco curado oftalmo).
 * Preparada para coexistir com futura importação CMED (source: 'cmed').
 */

export type MedicationCategory =
  | 'antiglaucoma'
  | 'antibiotic'
  | 'corticoid'
  | 'nsaid'
  | 'lubricant'
  | 'mydriatic'
  | 'antihistamine'
  | 'anesthetic'
  | 'other';

export type MedicationRoute =
  | 'topica_ocular'
  | 'oral'
  | 'intravenosa'
  | 'subconjuntival'
  | 'intravitrea';

export type MedicationEye =
  | 'ambos'
  | 'afetado'
  | 'operado'
  | 'direito'
  | 'esquerdo';

/**
 * Tipo de receituário exigido pela legislação brasileira (Portaria 344/98).
 * - `simples`: receituário comum, 1 via, sem retenção.
 * - `controle_especial`: receita branca em 2 vias (1ª retida na farmácia).
 *   Aplica-se a antibióticos (RDC 471/2021), Lista C1 (anticonvulsivantes,
 *   antiparkinsonianos), C4 (antirretrovirais), C5 (anabolizantes).
 *
 * Receitas A (amarela/entorpecente) e B (azul/psicotrópico) exigem
 * formulários numerados emitidos pela Vigilância Sanitária — não geramos.
 */
export type PrescriptionType = 'simples' | 'controle_especial';

export interface MedicationPosology {
  dose: string;
  frequency: string;
  duration: string;
  eye: MedicationEye;
}

export interface Medication {
  id: string;
  commercialName: string;
  activeIngredient: string;
  presentation: string;
  category: MedicationCategory;
  route: MedicationRoute;
  defaultPosology?: MedicationPosology;
  laboratory?: string;
  ean?: string;
  therapeuticClass?: string;
  source: 'curated' | 'cmed' | 'clinic';
  isFavorite: boolean;
  searchTokens: string[];
  /**
   * Override explícito do tipo de receituário. Quando ausente, o tipo é
   * derivado de `category` (antibiótico → controle_especial; resto → simples).
   * Útil para meds que `category` não captura (ex: anticonvulsivante C1).
   */
  prescriptionType?: PrescriptionType;
  /**
   * Presente apenas quando `source === 'clinic'`: o medicamento individual
   * salvo pela própria clínica (formato livre `AIMedication`, com posologia
   * humanizada, fórmula manipulada e quantidade). Quando o usuário escolhe um
   * item desses na busca, o editor usa este payload diretamente — em vez de
   * `curatedToAIMedication` — pra reconstruir a linha com fidelidade total.
   * Ver `services/firebase/clinicMedicationBank.ts`.
   */
  clinicAi?: AIMedication;
}

/**
 * Normaliza um termo de busca para casar com searchTokens (que foram gerados
 * pelo script de seed usando a mesma função de normalização).
 */
function normalize(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export const MedicationService = {
  /**
   * Busca medicamentos por token normalizado. Favoritos (curated) vêm antes,
   * depois fallback em não-favoritos (CMED, quando importado no futuro).
   * Requer índices compostos:
   *   (searchTokens array, isFavorite ASC, commercialName ASC)
   */
  async searchMedications(searchTerm: string, maxResults = 15): Promise<Medication[]> {
    const normalized = normalize(searchTerm);
    if (normalized.length < 2) return [];

    const token = normalized.split(/\s+/)[0];
    const col = collection(db, 'medications');

    const favoritesQ = query(
      col,
      where('searchTokens', 'array-contains', token),
      where('isFavorite', '==', true),
      orderBy('commercialName'),
      fsLimit(maxResults),
    );
    const favSnap = await getDocs(favoritesQ);
    const favorites = favSnap.docs.map(
      (d) => ({ id: d.id, ...(d.data() as Omit<Medication, 'id'>) }),
    );

    if (favorites.length >= maxResults) return favorites;

    // Completa com não-favoritos (só relevante quando CMED for importado)
    try {
      const extrasQ = query(
        col,
        where('searchTokens', 'array-contains', token),
        where('isFavorite', '==', false),
        orderBy('commercialName'),
        fsLimit(maxResults - favorites.length),
      );
      const extrasSnap = await getDocs(extrasQ);
      const extras = extrasSnap.docs.map(
        (d) => ({ id: d.id, ...(d.data() as Omit<Medication, 'id'>) }),
      );
      return [...favorites, ...extras];
    } catch {
      // Índice da segunda query pode não existir ainda; favoritos já bastam
      return favorites;
    }
  },
};

// ─── Helpers de apresentação (usados no autocomplete / form) ───

export const CATEGORY_LABELS: Record<MedicationCategory, string> = {
  antiglaucoma: 'Antiglaucomatoso',
  antibiotic: 'Antibiótico',
  corticoid: 'Corticoide',
  nsaid: 'AINE',
  lubricant: 'Lubrificante',
  mydriatic: 'Midriático',
  antihistamine: 'Antialérgico',
  anesthetic: 'Anestésico',
  other: 'Outro',
};

export const ROUTE_LABELS: Record<MedicationRoute, string> = {
  topica_ocular: 'Tópica ocular',
  oral: 'Oral',
  intravenosa: 'Intravenosa',
  subconjuntival: 'Subconjuntival',
  intravitrea: 'Intravítrea',
};

export const EYE_LABELS: Record<MedicationEye, string> = {
  ambos: 'ambos os olhos',
  afetado: 'olho afetado',
  operado: 'olho operado',
  direito: 'olho direito',
  esquerdo: 'olho esquerdo',
};

const OCULAR_ROUTES: ReadonlySet<MedicationRoute> = new Set<MedicationRoute>([
  'topica_ocular',
  'subconjuntival',
  'intravitrea',
]);

/**
 * Junta dose + frequência (+ olho, só em vias oculares) numa string amigável
 * para o campo "Frequência e Posologia" do receituário.
 * Ex (ocular): "1 gota, 4x ao dia, ambos os olhos"
 * Ex (oral):   "1 a 2 comprimidos, a cada 6 horas"
 *
 * `route` é opcional pra manter compat — sem route, comporta como antes (inclui olho).
 */
export function formatPosology(p: MedicationPosology, route?: MedicationRoute): string {
  const parts = [p.dose, p.frequency];
  if (!route || OCULAR_ROUTES.has(route)) {
    parts.push(EYE_LABELS[p.eye]);
  }
  return parts.filter(Boolean).join(', ');
}

export const PRESCRIPTION_TYPE_LABELS: Record<PrescriptionType, string> = {
  simples: 'Receituário Simples (1 via)',
  controle_especial: 'Receita de Controle Especial (2 vias)',
};

/**
 * Categorias que disparam Receita de Controle Especial automaticamente.
 * - antibiotic: RDC 471/2021 — antimicrobianos exigem 2 vias com retenção.
 */
const CONTROLLED_CATEGORIES: ReadonlySet<MedicationCategory> = new Set<MedicationCategory>([
  'antibiotic',
]);

/**
 * Resolve o tipo de receituário para um medicamento. Override explícito
 * (`prescriptionType` no doc) tem prioridade; senão deriva da categoria.
 */
export function derivePrescriptionType(
  category: MedicationCategory,
  explicit?: PrescriptionType,
): PrescriptionType {
  if (explicit) return explicit;
  return CONTROLLED_CATEGORIES.has(category) ? 'controle_especial' : 'simples';
}

/**
 * Palavras-chave que disparam Receita de Controle Especial 2 vias quando
 * encontradas em `nome`, `dosagem` ou `formula` de um item da receita.
 *
 * Cobertura:
 * - Antibióticos sistêmicos (RDC 471/2021).
 * - Opióides em associação (Portaria 344/98 art. 36 §1º — codeína ≤100mg).
 *
 * NÃO cobre psicotrópicos B1/B2 (azul) nem entorpecentes A (amarela) —
 * esses exigem Notificação de Receita numerada.
 *
 * Mantida em minúsculas e sem acentos pra casar com a normalização.
 */
const CONTROLLED_KEYWORDS: ReadonlyArray<string> = [
  // Antibióticos beta-lactâmicos
  'amoxicilina', 'amoxiclav', 'ampicilina', 'penicilina', 'oxacilina',
  'cefalexina', 'cefadroxil', 'cefuroxima', 'cefaclor', 'cefpodoxima',
  'cefepima', 'ceftriaxona', 'cefoxitina', 'cefotaxima', 'cefazolina',
  // Quinolonas
  'ciprofloxacino', 'norfloxacino', 'levofloxacino', 'moxifloxacino',
  'ofloxacino', 'gatifloxacino', 'gemifloxacino',
  // Macrolídeos
  'azitromicina', 'claritromicina', 'eritromicina', 'roxitromicina',
  'clindamicina', 'lincomicina',
  // Tetraciclinas
  'tetraciclina', 'doxiciclina', 'minociclina', 'tigeciclina',
  // Sulfas
  'sulfametoxazol', 'trimetoprima', 'bactrim', 'sulfa',
  // Aminoglicosídeos
  'gentamicina', 'tobramicina', 'amicacina', 'estreptomicina',
  // Outros
  'metronidazol', 'tinidazol', 'secnidazol',
  'nitrofurantoina', 'nitrofurantoína', 'fosfomicina',
  'vancomicina', 'teicoplanina', 'linezolida',
  'rifampicina', 'isoniazida',
  // Opióides em associação (≤100mg codeína por unid.) — controle especial branca 2 vias
  'codeina', 'codeína', 'codein', 'tylex', 'tramadol', 'tramal',
  // Antibióticos oftálmicos pelo NOME COMERCIAL. O modelo de pós-operatório
  // guarda "Cylocort" e não "ciprofloxacino + dexametasona" — e o princípio
  // ativo nem sempre vem preenchido. Sem isto, a receita do dia de cirurgia
  // saía como receituário simples com antibiótico dentro.
  'cylocort', 'ciloxan', 'biamotil', 'vigamox', 'zymar', 'zymaxid', 'oflox',
  'ocuflox', 'tobrex', 'tobradex', 'tobracin', 'maxitrol', 'maxiflox',
  'polytrim', 'cloranfenicol', 'dexafenicol', 'garasone', 'nepodex',
];

function normalizeKeyword(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

const NORMALIZED_KEYWORDS = CONTROLLED_KEYWORDS.map(normalizeKeyword);

/**
 * Heurística de detecção de medicamento controlado por nome/fórmula.
 * Usada quando o item da receita não veio do banco curado (manual ou template
 * antigo) e portanto não tem `prescriptionType` preenchido. Faz busca por
 * substring normalizada (sem acentos, lowercase) em todos os campos textuais.
 */
export function isControlledByText(...texts: Array<string | undefined>): boolean {
  const haystack = normalizeKeyword(texts.filter(Boolean).join(' '));
  if (!haystack) return false;
  return NORMALIZED_KEYWORDS.some((kw) => haystack.includes(kw));
}

/**
 * Decisão ÚNICA de "este item exige receita de controle especial": o campo
 * do banco curado OU a heurística por texto — que aqui inclui o PRINCÍPIO
 * ATIVO. Aceita tanto o item de rascunho (`nome`/`activeIngredient`) quanto
 * o registro salvo (`name`/`genericName`), porque os dois circulam. Todo
 * caminho que imprime ou grava receita deve perguntar aqui, e não repetir a
 * lista de campos — foi um caminho esquecendo o princípio ativo que deixou
 * o antibiótico passar como receita simples.
 */
export function isControlledMed(m: {
  nome?: string; name?: string;
  activeIngredient?: string; genericName?: string;
  dosagem?: string; dosage?: string;
  formula?: string;
  posologia?: string; frequency?: string;
  prescriptionType?: string;
}): boolean {
  if (m.prescriptionType === 'controle_especial') return true;
  return isControlledByText(
    m.nome || m.name,
    m.activeIngredient || m.genericName,
    m.dosagem || m.dosage,
    m.formula,
    m.posologia || m.frequency,
  );
}

// ─── Enrich AI extraction with curated bank ───

/**
 * Converte um item do banco curado num AIMedication com posologia padrão
 * humanizada. Mantida em sync com a versão duplicada em MedicationListEditor —
 * idealmente esta vira a única fonte; ali ainda existe pra evitar import
 * cíclico no editor.
 */
export function curatedToAIMedication(med: Medication): AIMedication {
  const p = med.defaultPosology;
  return {
    nome: med.commercialName,
    dosagem: med.presentation,
    posologia: p ? formatPosology(p, med.route) : '',
    duracao: p?.duration ?? '',
    via: ROUTE_LABELS[med.route],
    quantidade: med.route === 'oral' ? '1 caixa' : '1 frasco',
    category: med.category,
    prescriptionType: derivePrescriptionType(med.category, med.prescriptionType),
  };
}

// Palavras que aparecem nos nomes/dosagens vindas da IA mas não ajudam
// na busca do banco (forma farmacêutica / via). São puladas pra dar
// chance de tokens mais específicos como "latanoprosta" ou "timolol".
const SEARCH_STOPWORDS: ReadonlySet<string> = new Set([
  'colirio', 'colirios', 'gota', 'gotas', 'gel', 'creme', 'pomada',
  'solucao', 'suspensao', 'oftalmica', 'oftalmico', 'oftalmologica',
  'oral', 'orais', 'comprimido', 'comprimidos', 'capsula', 'capsulas',
  'frasco', 'frascos', 'caixa', 'caixas', 'tubo', 'mg', 'mcg', 'ml', 'ui',
  'para', 'com', 'sem', 'por', 'uso', 'topico', 'topica',
]);

/**
 * Quebra um texto em tokens (≥3 chars, sem acento) excluindo stopwords
 * de forma farmacêutica/via. Usado pra tentar achar o medicamento no
 * banco quando a IA escreve "Colírio de Latanoprosta 0,005%".
 */
function tokenizeForBankSearch(text: string): string[] {
  const norm = text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
  return norm
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= 3 && !SEARCH_STOPWORDS.has(t));
}

function normalizeForRank(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

/** Combinações (ex.: "Travoprosta + Timolol") devem ficar atrás de
 *  monocomponentes quando o usuário falou só um princípio. */
function isCombination(activeIngredient: string): boolean {
  return /\s*(\+|\/|\bcom\b|\be\b)\s*/i.test(activeIngredient);
}

/**
 * Pontua cada hit pela afinidade com o token buscado. Quanto maior, melhor.
 *
 * Hierarquia (do mais ao menos específico):
 *   100  commercialName === token exato                  (ex.: "Travatan")
 *    95  commercialName começa com token                 (ex.: "Travatan BAK")
 *    90  activeIngredient === token (monocomponente)     (ex.: "Travoprosta")
 *    80  activeIngredient começa com token (monocompon.) (ex.: "Travoprosta 0,004%")
 *    70  activeIngredient palavra inteira (monocompon.)
 *    60  commercialName contém token (substring)
 *    40  activeIngredient contém token (qualquer — combinações caem aqui)
 *    10  fallback (só searchTokens match)
 */
export function scoreHit(token: string, med: Medication): number {
  const t = normalizeForRank(token);
  const name = normalizeForRank(med.commercialName);
  const ingredient = normalizeForRank(med.activeIngredient);
  const combined = isCombination(med.activeIngredient);

  if (name === t) return 100;
  if (name.startsWith(t)) return 95;
  if (!combined && ingredient === t) return 90;
  if (!combined && ingredient.startsWith(t)) return 80;
  if (!combined && ingredient.split(/\s+/).includes(t)) return 70;
  if (name.includes(t)) return 60;
  if (ingredient.includes(t)) return 40;
  return 10;
}

/**
 * Tenta encontrar o melhor medicamento no banco curado pra o que a IA
 * extraiu. Para cada token candidato (palavras do `nome` e `dosagem`),
 * busca no Firestore (que casa por commercialName OU activeIngredient via
 * searchTokens) e ranqueia localmente — o índice retorna ordenado por
 * commercialName ASC, então sem rerank "DuoTrav" sai antes de "Travatan".
 *
 * Caso emblemático: IA escreve "Travoprosta" → buscamos por "travoprosta"
 * → hits incluem DuoTrav (Travoprosta+Timolol), Glaub (Travoprosta+Timolol),
 * Travatan (Travoprosta), Travoprosta Genérico. Sem rerank, devolveríamos
 * DuoTrav e o paciente receberia o combinado por engano. Com o rerank,
 * monocomponente (Travatan ou Genérico) ganha.
 */
/**
 * Mesma busca do `findInBank`, mas devolve a lista ranqueada INTEIRA com o
 * score de cada hit, em vez de só o vencedor.
 *
 * Existe porque o assistente de voz (spec 009) não pode aceitar o "melhor
 * palpite": quando dois medicamentos empatam no topo, ele precisa saber disso
 * pra virar lacuna e deixar o médico escolher (`resolveCatalogMatch` em
 * services/voice/normalize.ts). A extração por texto continua usando o top-1.
 */
export async function findBankCandidates(
  med: AIMedication,
): Promise<Array<{ med: Medication; score: number }>> {
  const tokens = [
    ...tokenizeForBankSearch(med.nome),
    ...tokenizeForBankSearch(med.dosagem),
  ];
  const seen = new Set<string>();
  for (const token of tokens) {
    if (seen.has(token)) continue;
    seen.add(token);
    try {
      // 15 resultados dá folga pro rerank (com 5 podia faltar o monocomponente
      // se a coleção tivesse muitas combinações antes alfabeticamente).
      const hits = await MedicationService.searchMedications(token, 15);
      if (hits.length === 0) continue;
      // Ordena por score desc, depois nome ASC pra desempate determinístico
      return hits
        .map((m) => ({ med: m, score: scoreHit(token, m) }))
        .sort((a, b) => {
          const diff = b.score - a.score;
          if (diff !== 0) return diff;
          return a.med.commercialName.localeCompare(b.med.commercialName, 'pt-BR');
        });
    } catch {
      // Firestore offline / sem índice → segue tentando os outros tokens
    }
  }
  return [];
}

async function findInBank(med: AIMedication): Promise<Medication | null> {
  const ranked = await findBankCandidates(med);
  return ranked[0]?.med ?? null;
}

/**
 * Enriquece a lista de medicamentos extraída pela IA com dados do banco
 * curado. Para cada item que casa com algum medicamento do banco:
 *   - Nome, dosagem (apresentação), via, categoria e prescriptionType
 *     vêm do banco — padroniza nome comercial e habilita auto-detecção
 *     de Receita de Controle Especial.
 *   - Posologia, duração e quantidade também vêm do banco — a receita sai
 *     como está no sistema; o que a IA ouviu só preenche campo que o banco
 *     deixou vazio. Pra mudar, o médico edita (pedido de 09/10/2026, mesma
 *     regra do ditado: `fromCadastro` em voice/resolveDrafts.ts).
 *   - `formula` (texto livre de manipulada) sempre vem da IA.
 *
 * Itens sem match no banco passam intactos — não é destrutivo.
 */
export async function enrichMedsWithBank(
  meds: AIMedication[],
): Promise<AIMedication[]> {
  if (!meds || meds.length === 0) return meds;
  return Promise.all(
    meds.map(async (med) => {
      if (!med.nome.trim()) return med;
      const hit = await findInBank(med);
      if (!hit) return med;
      const fromBank = curatedToAIMedication(hit);
      return {
        ...fromBank,
        posologia: fromBank.posologia.trim() || med.posologia.trim(),
        duracao: fromBank.duracao.trim() || med.duracao.trim(),
        quantidade: fromBank.quantidade.trim() || med.quantidade.trim(),
        formula: med.formula,
        ...desmameField(med),
      };
    }),
  );
}

// ─── Catálogo compacto pro prompt da IA ───

/**
 * Cache em módulo. O banco oftalmo curado é estático em runtime (só muda
 * por seed) — fetch único na primeira chamada vale a vida da aba.
 * Em caso de erro, zeramos a referência pra permitir retry na próxima
 * chamada.
 */
let bankCatalogCache: Promise<string> | null = null;

function formatBankForPrompt(meds: Medication[]): string {
  const ORDER: MedicationCategory[] = [
    'antiglaucoma',
    'antihistamine',
    'lubricant',
    'antibiotic',
    'corticoid',
    'nsaid',
    'mydriatic',
    'anesthetic',
    'other',
  ];
  const groups = new Map<MedicationCategory, string[]>();
  for (const m of meds) {
    const label = m.activeIngredient
      ? `${m.commercialName} (${m.activeIngredient})`
      : m.commercialName;
    const list = groups.get(m.category) || [];
    list.push(label);
    groups.set(m.category, list);
  }
  const lines: string[] = [];
  for (const cat of ORDER) {
    const items = groups.get(cat);
    if (!items || items.length === 0) continue;
    items.sort((a, b) => a.localeCompare(b, 'pt-BR'));
    lines.push(`- **${CATEGORY_LABELS[cat]}**: ${items.join(', ')}`);
  }
  return lines.join('\n');
}

/**
 * Carrega o banco curado e formata como string compacta agrupada por
 * categoria, pronta pra injetar no prompt da extração. Permite à IA
 * escolher o nome comercial certo mesmo quando o áudio sai com pronúncia
 * imperfeita (ex.: "patanose" → reconhece "Patanol S" da lista).
 *
 * Cache em módulo: a primeira chamada paga o fetch (uma vez por reload),
 * próximas resolvem imediatamente. Em caso de erro retorna string vazia
 * e zera o cache pra permitir retry na próxima extração.
 */
export function loadBankCatalogCompact(): Promise<string> {
  if (bankCatalogCache) return bankCatalogCache;
  bankCatalogCache = (async () => {
    try {
      const col = collection(db, 'medications');
      const q = query(col, where('isFavorite', '==', true));
      const snap = await getDocs(q);
      const meds = snap.docs.map(
        (d) => ({ id: d.id, ...(d.data() as Omit<Medication, 'id'>) }),
      );
      if (meds.length === 0) return '';
      return formatBankForPrompt(meds);
    } catch (err) {
      console.error('[medications] falha ao carregar banco pro prompt:', err);
      bankCatalogCache = null;
      return '';
    }
  })();
  return bankCatalogCache;
}
